import { Grid, makeStyles, Paper } from '@material-ui/core';
import { Apps } from '@material-ui/icons';
import { isEmpty } from 'lodash';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import BidBuyPackage from '../containers/BidBuyPackage';
import { SOURCES_BID_ALLOWED } from '../modules/bid-buy-helpers';
import {
  DetailCarDTO,
  IndividualBidStateInfoDTO,
  PackageDetails,
  SourceRegisterPotentialDTO,
  ValuationCountryCode,
  ValidityType,
} from '../modules/generated/api';

import BidInfoTable from './BidInfoTable';
import CarDetailTileUnit from './CarDetailTileUnit';
import PackageCarsList from './PackageCarsList';
import PackageDetailBasics from './PackageDetailBasics';
import PackageDetailBrandFuelInfo from './PackageDetailBrandFuelInfo';
import PackageDetailTileCo2 from './PackageDetailTileCo2';
import PackageDetailTileDamage from './PackageDetailTileDamage';
import PackageDetailTileFirstReg from './PackageDetailTileFirstReg';
import PackageDetailTileMileage from './PackageDetailTileMileage';
import PrevAuctionsDetailTable from './PrevAuctionsDetailTable';

type PackageDetailContentProps = {
  packageDetails: PackageDetails;
  comparisonCountries: Set<ValuationCountryCode>;
  setComparisonCountries: (countries: Set<ValuationCountryCode>) => void;
  countryPotentials: { [key: string]: SourceRegisterPotentialDTO };
  refresh: () => void;
};

const useStyles = makeStyles(() => ({
  tilesGrid: {
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
}));

const PackageDetailContent = ({
  packageDetails,
  comparisonCountries,
  setComparisonCountries,
  countryPotentials,
  refresh,
}: PackageDetailContentProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const showBidBuy =
    packageDetails.source &&
    SOURCES_BID_ALLOWED.includes(packageDetails.source!) &&
    !!packageDetails.marketingChannel?.length &&
    packageDetails.validity === ValidityType.Current;
  const {
    mileage,
    firstRegistration,
    brands,
    equipmentKeys,
    bid,
    source,
    dateEnd,
    previousAuctions,
    cars,
    engineCo2Nedc,
    engineCo2Wltp,
    priceDamage,
    engineFuel,
    offerId,
  } = packageDetails;
  const carCount = mileage?.count;
  const bidStateInfo: IndividualBidStateInfoDTO = packageDetails.bidStateInfo ?? {};
  const hasPreviousAuctions = !isEmpty(previousAuctions);
  const hasCars = !isEmpty(cars);
  const hasBidInfo =
    !isEmpty(bidStateInfo.allHistoricBids) ||
    !isEmpty(bidStateInfo.otherCurrentBids) ||
    !isEmpty(bidStateInfo.ownCurrentBids);
  const equipmentKeysArray = equipmentKeys ? Array.from(equipmentKeys) : [];
  let co2Standard: 'WLTP' | 'NEDC' | undefined;
  if (engineCo2Wltp) co2Standard = 'WLTP';
  else if (engineCo2Nedc) co2Standard = 'NEDC';

  const basicsGridMd = !showBidBuy && hasBidInfo ? 5 : 7;

  return (
    <Grid container spacing={3}>
      {/* Tiles full width */}
      <Grid item xs={12}>
        <Paper elevation={3}>
          <Grid container className={classes.tilesGrid}>
            <Grid item xs={6} md={4}>
              <CarDetailTileUnit value={carCount} unit="" label={t('common.car', { count: 2 })} icon={Apps} />
              <PackageDetailBrandFuelInfo brands={brands} engineFuel={engineFuel} />
            </Grid>

            <Grid item xs={6} md>
              <PackageDetailTileMileage mileage={mileage} />
            </Grid>

            <Grid item xs={6} md>
              <PackageDetailTileFirstReg firstRegistration={firstRegistration} />
            </Grid>

            <Grid item xs={6} md>
              <PackageDetailTileCo2 engineCo2={engineCo2Wltp ?? engineCo2Nedc} standard={co2Standard} />
            </Grid>

            <Grid item xs={6} md>
              <PackageDetailTileDamage priceDamage={priceDamage} />
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={12}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={basicsGridMd}>
                <PackageDetailBasics
                  packageDetails={packageDetails}
                  comparisonCountries={comparisonCountries}
                  setComparisonCountries={setComparisonCountries}
                  countryPotentials={countryPotentials}
                  equipmentKeys={equipmentKeysArray}
                />
              </Grid>
              {showBidBuy ? (
                <Grid item xs={12} md={5}>
                  <BidBuyPackage packageDetails={packageDetails} refresh={refresh} />
                </Grid>
              ) : hasBidInfo ? (
                <Grid item xs={12} md={7}>
                  <BidInfoTable
                    bids={bidStateInfo}
                    title={t('bids.bidHammers.bidsOverview')}
                    source={source}
                    refresh={refresh}
                    dateEnd={dateEnd}
                    bidId={bid?.id}
                  />
                </Grid>
              ) : null}
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {hasCars && (
        <Grid item xs={12}>
          <PackageCarsList cars={cars} title={`${t('common.car', { count: 2 })} (${cars?.length})`} />
        </Grid>
      )}

      {showBidBuy && hasBidInfo && (
        <Grid item xs={12}>
          <BidInfoTable
            bids={bidStateInfo}
            title={t('bids.bidHammers.bidsOverview')}
            source={source}
            refresh={refresh}
            dateEnd={dateEnd}
            bidId={bid?.id}
          />
        </Grid>
      )}

      {hasPreviousAuctions && (
        <Grid item xs={12}>
          <PrevAuctionsDetailTable
            title={`${t('bids.prevAuctions')} (${previousAuctions?.length})`}
            car={{ previousAuctions } as DetailCarDTO}
            variant="package"
            packageOfferId={offerId}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default memo(PackageDetailContent);
